import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Image as ImageIcon, FileText, ShieldCheck, Wine, UserCheck } from "lucide-react";
import api, { API } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { PRODUCT_SHEET_FIELDS } from "./admin/product-sheet-fields";
import PhotoGallery from "./bottle/PhotoGallery";
import Traceability from "./bottle/Traceability";

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

function Field({ label, value, multiline }) {
  return (
    <div
      className={`py-3 border-b ${multiline ? "sm:col-span-2" : ""}`}
      style={{ borderColor: "var(--vault-border)" }}
    >
      <p className="overline text-[var(--vault-text-muted)] mb-1">{label}</p>
      <p className={`text-sm text-white ${multiline ? "whitespace-pre-wrap break-words" : "break-words"}`}>
        {value || <span className="text-[var(--vault-text-faint)]">—</span>}
      </p>
    </div>
  );
}

export default function Bottle() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [params] = useSearchParams();
  const token = params.get("t");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [trace, setTrace] = useState([]);

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
    let cancelled = false;
    api.get(`/bottle?token=${encodeURIComponent(token)}`)
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => !cancelled && setError(e?.response?.data?.detail || "Unable to load bottle"));
    api.get(`/bottle/traceability?token=${encodeURIComponent(token)}`)
      .then((r) => !cancelled && setTrace(r.data))
      .catch(() => !cancelled && setTrace([]));
    return () => { cancelled = true; };
  }, [token, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--vault-bg)" }}>
        <div className="text-center max-w-md px-6">
          <p className="font-display text-3xl mb-3">Unable to load this bottle</p>
          <p className="text-sm text-[var(--vault-text-muted)] mb-6">{error}</p>
          <button onClick={() => navigate("/")} className="btn-gold px-5 py-2.5 rounded-md text-sm" data-testid="bottle-back-home">
            Back to portal
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--vault-bg)" }}>
        <span className="spin" style={{ borderTopColor: "var(--vault-gold)" }} />
      </div>
    );
  }

  const sheet = data.product_sheet;
  const hasSheet = Boolean(sheet);
  const hasPhotos = data.photos && data.photos.length > 0;
  const hasCert = Boolean(data.file_id);

  const openCert = () => {
    navigate(`/view?file=${data.file_id}&t=${encodeURIComponent(token)}&name=${encodeURIComponent(data.file_name || "")}`);
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--vault-bg)" }} data-testid="bottle-page">
      {/* Top bar */}
      <header
        className="border-b sticky top-0 z-10"
        style={{ borderColor: "var(--vault-border)", background: "rgba(10,10,12,0.92)", backdropFilter: "blur(12px)" }}
      >
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="btn-ghost inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm" data-testid="bottle-back">
            <ArrowLeft className="w-4 h-4" /> Portal
          </button>
          <div className="flex items-center gap-3">
            <img src={settings.logo_url} alt={settings.brand_name} className="w-8 h-8 object-contain" style={{ mixBlendMode: "lighten" }} />
            <span className="font-display text-lg hidden sm:inline">{settings.brand_name}</span>
          </div>
          <a
            href={`/verify?code=${encodeURIComponent(data.code)}`}
            target="_blank"
            rel="noreferrer"
            className="btn-ghost inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs"
            data-testid="bottle-verify-link"
          >
            <ShieldCheck className="w-3.5 h-3.5" style={{ color: "var(--vault-gold)" }} />
            <span className="hidden sm:inline">Verify externally</span>
          </a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 sm:py-14">
        {/* Hero */}
        <div className="text-center mb-12 fade-up">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-5" style={{ background: "rgba(197,160,89,0.12)", border: "1px solid rgba(197,160,89,0.3)" }}>
            <Wine className="w-7 h-7" style={{ color: "var(--vault-gold)" }} />
          </div>
          <p className="overline text-[var(--vault-text-muted)] mb-2">Bottle Passport · <span className="font-mono-vault text-[var(--vault-gold)]">{data.code}</span></p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">
            {sheet?.wine_name || data.label || "Authenticated Bottle"}
          </h1>
          {sheet?.producer && (
            <p className="text-[var(--vault-text-muted)] mt-3 text-lg">
              {sheet.producer}{sheet.vintage ? ` · ${sheet.vintage}` : ""}
            </p>
          )}
          {data.owner_count > 0 && (
            <p className="inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mt-5 px-4 py-1.5 rounded-full text-xs text-[var(--vault-text-muted)]" style={{ border: "1px solid rgba(197,160,89,0.3)", background: "rgba(197,160,89,0.06)" }} data-testid="bottle-provenance">
              <UserCheck className="w-3.5 h-3.5" style={{ color: "var(--vault-gold)" }} />
              {data.owner_since ? <span>Owner since <span className="text-white whitespace-nowrap">{new Date(data.owner_since).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}</span></span> : <span>Registered owner</span>}
              <span className="text-[var(--vault-text-faint)]">·</span>
              <span className="text-white whitespace-nowrap" data-testid="bottle-owner-ordinal">{ordinal(data.owner_count)} owner</span>
            </p>
          )}
        </div>

        {/* Product sheet grid */}
        {hasSheet ? (
          <div
            className="rounded-2xl p-6 sm:p-10 mb-10 fade-up delay-1"
            style={{ background: "var(--vault-surface)", border: "1px solid var(--vault-border)" }}
            data-testid="bottle-sheet"
          >
            <p className="overline text-[var(--vault-text-muted)] mb-6">Product Sheet</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-0">
              {PRODUCT_SHEET_FIELDS.map((f) => (
                <Field key={f.key} label={f.label} value={sheet[f.key]} multiline={f.multiline} />
              ))}
            </div>
          </div>
        ) : (
          <div
            className="rounded-2xl p-6 sm:p-8 mb-10 text-center fade-up delay-1"
            style={{ background: "var(--vault-surface)", border: "1px dashed var(--vault-border)" }}
          >
            <p className="text-sm text-[var(--vault-text-muted)]">
              No product sheet has been published for this bottle yet.
            </p>
          </div>
        )}

        <Traceability events={trace} />

        {/* Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 fade-up delay-2">
          <button
            onClick={() => setGalleryOpen(true)}
            disabled={!hasPhotos}
            data-testid="bottle-open-photos"
            className="action-card group disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ImageIcon className="w-7 h-7" style={{ color: "var(--vault-gold)" }} />
            <div className="text-left">
              <p className="font-display text-xl">Open photos</p>
              <p className="text-xs text-[var(--vault-text-muted)] mt-1">
                {hasPhotos ? `${data.photos.length} bottle photo${data.photos.length === 1 ? "" : "s"}` : "No photos available"}
              </p>
            </div>
          </button>
          <button
            onClick={openCert}
            disabled={!hasCert}
            data-testid="bottle-open-cert"
            className="action-card group disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileText className="w-7 h-7" style={{ color: "var(--vault-gold)" }} />
            <div className="text-left">
              <p className="font-display text-xl">Open digital certificate</p>
              <p className="text-xs text-[var(--vault-text-muted)] mt-1">
                {hasCert ? data.file_name : "No certificate attached"}
              </p>
            </div>
          </button>
        </div>

        <p className="text-center overline text-[var(--vault-text-faint)] mt-14">
          Verified · Tamper-evident · {data.brand_name}
        </p>
      </main>

      {galleryOpen && hasPhotos && (
        <PhotoGallery photos={data.photos} token={token} onClose={() => setGalleryOpen(false)} />
      )}

      <style>{`
        .action-card {
          display: flex;
          align-items: center;
          gap: 1.25rem;
          padding: 1.75rem;
          border-radius: 1rem;
          background: var(--vault-surface);
          border: 1px solid var(--vault-border);
          transition: border-color .25s ease, transform .25s ease;
          width: 100%;
        }
        .action-card:not(:disabled):hover {
          border-color: var(--vault-gold);
          transform: translateY(-2px);
        }
      `}</style>
    </div>
  );
}
